import type {ReactElement} from 'react';
import {useAiChat} from '@AiChat/providers/ai-chat.context';
import {AiChatHistoryDrawer} from '@AiChat/components/ai-chat-history-drawer/ai-chat-history-drawer';
import type {AiChatProps} from '@AiChat/ai-chat.types';
import {AI_CHAT_HOME_SCROLL_ID} from '@AiChat/ai-chat.types';
import {AiChatDragDropOverlay} from '@AiChat/components/ai-chat-drag-drop-overlay/ai-chat-drag-drop-overlay';
import {AiChatBanner} from '@AiChat/components/ai-chat-banner/ai-chat-banner';
import {AiChatPageContent} from '@AiChat/components/ai-chat-page-content/ai-chat-page-content';
import {AiChatRoot} from '@AiChat/ai-chat-root';
import {useAiChatLayout} from '@AiChat/hooks/useAiChatLayout';
import {useAiChatPageClassNames} from '@AiChat/hooks/useAiChatPageClassNames';

function AiChatPage({chatOnLoadParams, contextProfile, banner}: AiChatProps): ReactElement {
  const {state} = useAiChat();
  const {isMobileLayout} = useAiChatLayout();
  const classNames = useAiChatPageClassNames();

  const renderDesktopHistoryDrawer = (): ReactElement | null => {
    if (isMobileLayout) {
      return null;
    }

    return <AiChatHistoryDrawer />;
  };

  const renderMobileHistoryDrawer = (): ReactElement | null => {
    if (!isMobileLayout) {
      return null;
    }

    return <AiChatHistoryDrawer />;
  };

  const renderBanner = (): ReactElement | null => {
    if (!banner) {
      return null;
    }

    return <AiChatBanner banner={banner} />;
  };

  const renderDesktopDragDropOverlay = (): ReactElement | null => {
    if (isMobileLayout) {
      return null;
    }

    return <AiChatDragDropOverlay promptBoxId={state.promptBoxId} />;
  };

  return (
    <div id={AI_CHAT_HOME_SCROLL_ID} className={classNames.fullScreen}>
      {renderBanner()}
      {renderDesktopHistoryDrawer()}
      <AiChatPageContent chatOnLoadParams={chatOnLoadParams} contextProfile={contextProfile} className={classNames.pageContent} conversationClassName={classNames.conversation} />
      {renderMobileHistoryDrawer()}
      {renderDesktopDragDropOverlay()}
    </div>
  );
}

export function AiChat(props: AiChatProps): ReactElement {
  return (
    <AiChatRoot chatProps={props} isEmbedded={false}>
      <AiChatPage {...props} />
    </AiChatRoot>
  );
}
